<template>
  <div id="app">
    <el-config-provider :locale="elLocale[locale] ?? en">
      <metainfo />
      <router-view></router-view>
      <HotkeyHelpOverlay v-model="isHelpVisible" />
      <LeafletCreationPanel />
      <LocationGroupsManager
        v-if="locationGroupsManagerStore"
        v-model="locationGroupsManagerVisible"
        :store-id="locationGroupsManagerStore.id"
        :store-name="locationGroupsManagerStore.name"
        :repository="locationGroupsRepository"
        :confirm-delete="confirmLocationGroupDelete"
      />
    </el-config-provider>
  </div>
</template>

<script>
import { computed, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMeta } from 'vue-meta'
import { useStore } from 'vuex'
import { dayjs, ElConfigProvider, ElMessageBox } from 'element-plus'

import en from 'element-plus/dist/locale/en.mjs'
import pl from 'element-plus/dist/locale/pl.mjs'
import ru from 'element-plus/dist/locale/ru.mjs'

import HotkeyHelpOverlay from '@/components/HotkeyHelpOverlay/HotkeyHelpOverlay.vue'
import { useHotkeyHelp } from '@/components/HotkeyHelpOverlay/useHotkeyHelp'
import LeafletCreationPanel from '@/modules/leaflet-creation/LeafletCreationPanel.vue'
import LocationGroupsManager from '@/modules/location-groups/components/LocationGroupsManager.vue'
import { provideLocationGroupsUiContext } from '@/modules/location-groups/composables/useLocationGroupsUiContext'
import { createHttpLocationGroupsRepository } from '@/modules/location-groups/infrastructure/httpLocationGroupsRepository'

export default {
  name: 'App',
  components: { HotkeyHelpOverlay, ElConfigProvider, LocationGroupsManager, LeafletCreationPanel },
  setup() {
    const domain = window && window.location.hostname
    useMeta({
      title: `${import.meta.env.VITE_APP_TITLE} ${domain}`
    })

    const { isHelpVisible } = useHotkeyHelp()
    const { locale, t } = useI18n()
    const store = useStore()
    const elLocale = { pl, en, ru }
    const locationGroupsRepository = createHttpLocationGroupsRepository()
    const locationGroupsUi = provideLocationGroupsUiContext({
      repository: locationGroupsRepository,
      roles: computed(() => store.getters.roles || []),
      confirmDelete: async group => {
        try {
          await ElMessageBox.confirm(t('locationGroups.deleteConfirm', { name: group.name }))
          return true
        } catch {
          return false
        }
      },
      confirmReplacement: async () => {
        try {
          await ElMessageBox.confirm(t('locationGroups.selector.confirmReplace'))
          return true
        } catch {
          return false
        }
      }
    })

    watchEffect(() => {
      switch (locale.value) {
        case 'pl':
          import('dayjs/locale/pl').then(() => {
            dayjs.locale('pl')
          })
          break
        case 'ru':
          import('dayjs/locale/ru').then(() => {
            dayjs.locale('ru')
          })
          break
        default:
          import('dayjs/locale/en-gb').then(() => {
            dayjs.locale('en-gb')
            dayjs.Ls.en.weekStart = 1
          })
      }
    })

    return {
      isHelpVisible,
      locale,
      elLocale,
      locationGroupsRepository,
      locationGroupsManagerStore: locationGroupsUi.managerStore,
      locationGroupsManagerVisible: locationGroupsUi.managerVisible,
      confirmLocationGroupDelete: locationGroupsUi.confirmDelete
    }
  }
}
</script>
